import { Grid, Stack, Typography, useTheme } from "@mui/material";
import { ReactComponent as StackIcon } from "Components/_Shared/Icons/Assets/stack.svg";
import { ReactComponent as UserIcon } from "Components/_Shared/Icons/Assets/user-male.svg";
import { useGetAnalysisClients } from "Infrastructure/ApiZfp/AnalyzerApi";
import type { LiabilityItem } from "Infrastructure/ApiZfp/Schema";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { LIABILITY_SUBTYPE_RESOURCE_KEY } from "Infrastructure/Utils/AssetUtils";
import { formatClientName } from "Infrastructure/Utils/ClientUtils";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { type FC, useContext } from "react";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { Tag } from "../_Shared/Tag";

type Props = {
  readonly liability: LiabilityItem;
  readonly onEdit: () => void;
};

export const LiabilityTile: FC<Props> = ({ liability, onEdit }) => {
  const { t } = useResource();
  const theme = useTheme();
  const { versionId, analysisId } = useContext(AnalysisDetailContext);

  const { data: clientsData } = useGetAnalysisClients(analysisId, versionId);

  const client = clientsData?.clients.find((client) => client.guid === liability?.clientGuid);

  return (
    <Stack
      direction="row"
      justifyContent="flex-start"
      alignItems="flex-start"
      gap={Spacing.L}
      sx={{
        border: `1px solid ${theme.colors.borderLight}`,
        borderRadius: theme.spacing(Radius.M),
        padding: theme.spacing(Spacing.L),
        boxShadow: theme.shadows[1],
        background: theme.colors.white,
        cursor: "pointer",
      }}
      onClick={onEdit}
    >
      <Stack spacing={Spacing.M} width="100%" direction="column" justifyContent="flex-start" mt={theme.spacing(Spacing.XS)}>
        <Stack spacing={Spacing.XS}>
          <Typography variant="body2" color={theme.colors.textPrimary} fontWeight={600}>
            {liability.name}
          </Typography>
          <Typography variant="caption" color={theme.colors.textSecondary} fontWeight={400}>
            {liability.note}
          </Typography>
        </Stack>

        <Grid container gap={Spacing.M}>
          <Tag
            variant="solid"
            color="grey"
            title={t(Resources.AnalysisDetail.Assets.Liabilities.LiabilityType[LIABILITY_SUBTYPE_RESOURCE_KEY[liability.liabilitySubType]])}
          />
          <Tag
            variant="solid"
            color="grey"
            icon={<StackIcon />}
            title={formatCurrency(liability.amount.value, liability.amount.currencyCode)}
          />
          {client && (
            <Tag
              variant="solid"
              color="info"
              title={formatClientName(client.firstName, client.surname, null)}
              icon={<UserIcon width={12} height={12} />}
            />
          )}
        </Grid>
      </Stack>
    </Stack>
  );
};
